import { fetchWithChatFeedback } from '../../api/chatFeedbackRequired';
/**
 * `useTurnProgress` — React hook polling a v2-turn server-side progress
 * envelope for a single in-flight turn.
 *
 * The backend persists progress under `data/progress/{client_request_id}.json`
 * and exposes a single JSON polling endpoint:
 *   - `GET /api/{far|home}-chat/progress/{crid}`
 *
 * The hook polls this endpoint at a short interval (1s by default) and
 * stops when the envelope reports `done=true` or after 5 consecutive
 * fetch failures (raises a generic error). The failure counter is only
 * armed AFTER the first successful poll (i.e. once `hasServerData` is
 * true): before that we are in the relevance/clarification gate window
 * where the endpoint legitimately 404s, so those failures are ignored to
 * avoid stopping the poller before the readers ever start. The previous
 * SSE seam was
 * removed: the existing `BaseHTTPMiddleware` buffers streaming
 * responses, so SSE never delivered live events anyway, and polling at
 * 1s is indistinguishable from SSE for a 30s turn.
 *
 * See:
 *   - issues/20260505-turn-progress-checklist.md §4.2.2
 */
import { useEffect, useRef, useState } from 'react';

export type StepState = 'pending' | 'running' | 'done' | 'failed';

export interface ProgressStep {
  step_id: string;
  label: string;
  state: StepState;
}

export interface ProgressErrorShape {
  step_id: string;
  message: string;
}

export interface ProgressState {
  steps: ProgressStep[];
  /** Server-selected active operation; use only when its step is running. */
  current_step_id: string | null;
  done: boolean;
  error: ProgressErrorShape | null;
  /**
   * True once at least one successful poll has merged a server snapshot
   * into local state. Until then the steps array is EMPTY — that window is
   * the relevance/clarification gate, which runs server-side BEFORE
   * `init_turn_progress`, so no progress envelope exists yet and polls
   * 404 / return nothing useful. During the gate the checklist shows ONLY
   * the client-only "Reading your question" lead step (running); the
   * reader steps are intentionally suppressed until the readers actually
   * start. Containers use this flag to drive the lead step: it is
   * `running` while `!hasServerData` and `done` once the first server
   * snapshot arrives (readers started → reader steps now render).
   */
  hasServerData: boolean;
}

/**
 * Surface enum: which `/api/<surface>/progress/...` mount to poll.
 * Far-chat and home-chat expose identical wire shapes; the only
 * difference is the URL prefix.
 */
export type ProgressSurface = 'far-chat' | 'home-chat';

export interface UseTurnProgressOptions {
  /** Which API mount to use. Defaults to `'far-chat'`. */
  surface?: ProgressSurface;
  /** Base URL prefix (e.g. `''` for same-origin). Defaults to `''`. */
  baseUrl?: string;
  /** Called once when the run terminates (envelope reports done=true). */
  onComplete?: () => void;
  /**
   * Optional injection point for tests. Same shape as `fetch`; defaults
   * to `globalThis.fetch`.
   */
  fetchImpl?: typeof fetch;
  /**
   * Polling interval in ms. Defaults to 1000.
   */
  pollIntervalMs?: number;
}

const EMPTY_STATE: ProgressState = {
  steps: [],
  current_step_id: null,
  done: false,
  error: null,
  hasServerData: false,
};

/**
 * Canonical reader-step ids + labels the backend emits once answer
 * generation begins (i.e. once `init_turn_progress` has run and the first
 * progress envelope exists). These are NO LONGER painted optimistically on
 * submit: during the relevance/clarification gate phase (before any server
 * snapshot) the checklist shows ONLY the "Reading your question" lead step,
 * and these reader rows appear solely from server snapshots. Retained as an
 * exported reference for tests and for label/id stability.
 */
export const CANONICAL_STEPS: ProgressStep[] = [
  { step_id: 'read_far', label: 'Reading the RFO Parts 1-51, 53', state: 'pending' },
  { step_id: 'read_part52', label: 'Reading the RFO Part 52', state: 'pending' },
  // `narrow_focus` ("Narrowing focus") is emitted by the backend on the
  // FIRST request (the post-reader topic-focus gate) right before the synth.
  // Mirror it here, inserted BEFORE `synth_deviations`, so the pre-server
  // fallback list stays consistent with the server-sent steps and the
  // checklist doesn't flicker. The focus-resubmit envelope instead carries a
  // single `synth_deviations` step. See Bug 2 progress-mirror fix.
  { step_id: 'narrow_focus', label: 'Narrowing focus', state: 'pending' },
  { step_id: 'synth_deviations', label: 'Reading DoD Deviations', state: 'pending' },
];

/**
 * Legacy-FAR reader step ids + labels. The backend emits these (in place of
 * — or, for the "Both" FAR version, alongside — the RFO reader steps) once
 * the active FAR version selected in the clarification card is Legacy or
 * Both. The actual rows still render from the polled server `envelope.steps`
 * (which is regime-aware on the backend); these constants exist for
 * label/id stability and tests, mirroring `CANONICAL_STEPS`. See
 * issues/20260612-legacy-far-readers_spec.md §4.4.
 */
/**
 * The legacy non-52 reader is sharded into two part-range shards at the
 * Part-26 / subchapter-D|E seam (Legacy FAR Reader Sharding, spec
 * issues/20260613-legacy-far-reader-sharding): the legacy corpus exceeds
 * the Gemini window in one prefix, so Reader A runs as two LLM calls. The
 * backend dispatcher emits BOTH shard ids; these constants mirror that
 * shared contract for label/id stability and tests.
 */
export const READ_FAR_LEGACY_1_STEP: ProgressStep = {
  step_id: 'read_far_legacy_1',
  label: 'Reading the Legacy FAR Parts 1-26',
  state: 'pending',
};
export const READ_FAR_LEGACY_2_STEP: ProgressStep = {
  step_id: 'read_far_legacy_2',
  label: 'Reading the Legacy FAR Parts 27-51, 53',
  state: 'pending',
};
export const READ_PART52_LEGACY_STEP: ProgressStep = {
  step_id: 'read_part52_legacy',
  label: 'Reading the Legacy FAR Part 52',
  state: 'pending',
};

/**
 * Canonical reader steps for the Legacy FAR version (single-regime). The
 * non-52 reader is split into two part-range shard rows. The synth step id
 * is shared with the RFO list (`synth_deviations`).
 */
export const CANONICAL_STEPS_LEGACY: ProgressStep[] = [
  READ_FAR_LEGACY_1_STEP,
  READ_FAR_LEGACY_2_STEP,
  READ_PART52_LEGACY_STEP,
  { step_id: 'synth_deviations', label: 'Reading DoD Deviations', state: 'pending' },
];

/**
 * Canonical reader steps for the "Both" FAR version — the RFO reader pair,
 * then the two Legacy Reader-A shard rows + the Legacy Part-52 row, followed
 * by the shared synth step.
 */
export const CANONICAL_STEPS_BOTH: ProgressStep[] = [
  { step_id: 'read_far', label: 'Reading the RFO Parts 1-51, 53', state: 'pending' },
  { step_id: 'read_part52', label: 'Reading the RFO Part 52', state: 'pending' },
  READ_FAR_LEGACY_1_STEP,
  READ_FAR_LEGACY_2_STEP,
  READ_PART52_LEGACY_STEP,
  { step_id: 'synth_deviations', label: 'Reading DoD Deviations', state: 'pending' },
];

/**
 * Stable step id + label for the client-only "Reading your question"
 * lead step shown FIRST on the checklist while the relevance/clarification
 * gate runs (a phase that precedes any server progress envelope).
 */
export const LEAD_STEP_ID = 'read_question';
export const LEAD_STEP_LABEL = 'Reading your question';

/**
 * Build the "Reading your question" lead step for the in-flight checklist.
 *
 * It is `running` until the first server snapshot arrives (the gate is
 * still classifying / about to dispatch the readers), then `done`. Returns
 * `null` when there is no in-flight turn so callers can pass it straight
 * through to `StepProgressChecklist`'s optional `leadStep` prop.
 */
export function buildLeadStep(
  inFlight: boolean,
  hasServerData: boolean,
  label: string = LEAD_STEP_LABEL
): ProgressStep | null {
  if (!inFlight) return null;
  return {
    step_id: LEAD_STEP_ID,
    label,
    state: hasServerData ? 'done' : 'running',
  };
}

function makeCanonicalPendingState(): ProgressState {
  // Pre-server seed: while `hasServerData` is false we are in the
  // relevance/clarification GATE phase — the backend runs the gate BEFORE
  // `init_turn_progress`, so no progress envelope exists yet. During this
  // window the reader steps have NOT started, so we must NOT paint them.
  // We seed an EMPTY `steps` array and let the client-only "Reading your
  // question" lead step (driven by `buildLeadStep`) be the sole running
  // row. The reader steps appear only once the first server snapshot
  // arrives (readers actually started) — see `tick()` setting
  // `hasServerData: true`. This holds for both the already-clear path and
  // the clarified path (in both, the gate precedes the readers).
  return {
    steps: [],
    current_step_id: null,
    done: false,
    error: null,
    // No server snapshot has been merged yet — we're in the gate window.
    hasServerData: false,
  };
}

/** Stop polling after this many consecutive failures. */
const POLL_FAILURE_LIMIT = 5;

/**
 * Subscribe to the progress envelope for `clientRequestId`. When `null`,
 * the hook is dormant (no polling, empty state).
 *
 * Returns the live `{ steps, done, error }` envelope. Each successful
 * poll replaces the local state with the server snapshot. Polling stops
 * once the server reports `done=true` or after 5 consecutive failures.
 */
export function useTurnProgress(
  clientRequestId: string | null,
  options: UseTurnProgressOptions = {}
): ProgressState {
  const {
    surface = 'far-chat',
    baseUrl = '',
    onComplete,
    fetchImpl,
    pollIntervalMs = 1000,
  } = options;

  // Initial state: when mounted with a non-null crid, pre-populate with
  // the canonical 3 pending steps so the bubble renders the checklist on
  // first paint (before the first poll completes). When mounted dormant
  // (crid=null), stay empty.
  const [state, setState] = useState<ProgressState>(() =>
    clientRequestId ? makeCanonicalPendingState() : EMPTY_STATE
  );

  // Latest `onComplete` ref so the effect doesn't tear down + rebuild
  // on every parent render.
  const onCompleteRef = useRef<typeof onComplete>(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!clientRequestId) {
      setState(EMPTY_STATE);
      return;
    }

    // Reset to the canonical 3 pending steps whenever a new turn begins
    // (clientRequestId changes). This guarantees a fresh turn never
    // inherits the previous turn's `done`/`error`/step-state mid-flight,
    // and the bubble shows all-pending before the first poll arrives.
    setState(makeCanonicalPendingState());

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let pollFailures = 0;
    let completedFired = false;
    // Tracks whether at least one successful poll has merged a server
    // snapshot for THIS turn. While false we are in the pre-envelope
    // relevance/clarification gate window: the backend runs the gate
    // BEFORE `init_turn_progress`, so the progress endpoint legitimately
    // 404s for the entire (potentially ~10s+) gate. Failed polls during
    // that window are EXPECTED ("not ready yet") and must NOT count toward
    // the hard-stop `POLL_FAILURE_LIMIT` — otherwise the poller stops
    // permanently before the readers ever start. A local flag (not React
    // state) is used so the failure path reads a fresh, closure-stable
    // value with no risk of a stale render snapshot. The hard-stop
    // protection re-activates once this flips true (genuine mid-stream
    // connection loss after the envelope exists).
    let hasServerDataLocal = false;

    const progressUrl = `${baseUrl}/api/${surface}/progress/${encodeURIComponent(
      clientRequestId
    )}`;
    // A proxy/browser cache can otherwise keep returning the first envelope
    // for a long-running request id.  Keep both a request cache directive and
    // a distinct URL per poll; the server also sends Cache-Control: no-store.
    let pollSequence = 0;

    const fireComplete = () => {
      if (completedFired) return;
      completedFired = true;
      try {
        onCompleteRef.current?.();
      } catch {
        // Swallow — caller errors must not poison teardown.
      }
    };

    const stopPolling = () => {
      if (pollTimer !== null) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        const pollUrl = `${progressUrl}?poll=${++pollSequence}`;
        const resp = await fetchWithChatFeedback(pollUrl, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        }, fetchImpl);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const body = (await resp.json()) as Partial<ProgressState> & {
          steps?: ProgressStep[];
          current_step_id?: unknown;
        };
        if (cancelled) return;
        pollFailures = 0;
        const incoming = body.steps ?? [];
        // A 200 is NOT by itself proof the gate is past. The backend can
        // return a 200 envelope whose `steps` array is EMPTY — e.g. a
        // done/cleanup envelope, or one written before the reader steps
        // are seeded. Treating that as "server data arrived" would flip
        // the lead "Reading your question" step to done with NO reader
        // rows, making the checklist look finished while the turn is still
        // generating. A "meaningful snapshot" is one that either carries
        // real reader steps (non-empty `steps`) OR is a genuine terminal
        // `done` signal. `hasServerData` (state) and `hasServerDataLocal`
        // (the failure-gate flag) flip together on exactly this condition
        // so the pre-envelope failure gating stays aligned.
        const meaningfulSnapshot = incoming.length > 0 || !!body.done;
        // The gate is past only on a meaningful snapshot. From here on,
        // failed polls represent real mid-stream connection loss and DO
        // count toward the hard-stop limit. An empty, non-terminal 200
        // leaves us in the gate window (still expected to 404/empty).
        if (meaningfulSnapshot) {
          hasServerDataLocal = true;
        }
        // Merge by step_id: backend writes the full envelope on every
        // mutation, so an outright replace would also work — but merging
        // by step_id keeps any client-only fields stable should the
        // shape ever drift.
        setState((prev) => {
          // On an empty, non-terminal 200 keep the prior steps untouched:
          // an empty `incoming` must NOT clobber already-rendered reader
          // rows, and must NOT seed an empty array as if the gate ended.
          if (!meaningfulSnapshot) {
            return prev;
          }
          const byId = new Map(incoming.map((s) => [s.step_id, s]));
          const mergedSteps =
            prev.steps.length === 0
              ? incoming
              : prev.steps.map((s) => byId.get(s.step_id) ?? s);
          // Append any incoming steps the previous state didn't have
          // (defensive — shouldn't happen with a stable canonical list).
          for (const s of incoming) {
            if (!mergedSteps.some((m) => m.step_id === s.step_id)) {
              mergedSteps.push(s);
            }
          }
          return {
            steps: mergedSteps,
            current_step_id: typeof body.current_step_id === 'string'
              ? body.current_step_id
              : null,
            done: !!body.done,
            error: body.error ?? null,
            // A meaningful snapshot merged server data — the gate is
            // definitively past, so the "Reading your question" lead
            // step can flip to done.
            hasServerData: true,
          };
        });
        if (body.done) {
          fireComplete();
          stopPolling();
          return;
        }
      } catch {
        // Pre-envelope gate window: the progress endpoint legitimately
        // 404s (and a flaky network can also fail) until the readers
        // start. Treat any failure here as a benign "not ready yet" — keep
        // polling and do NOT advance the hard-stop counter. The turn's
        // lifecycle still bounds this: when the awaited POST resolves
        // (answer OR `clarification_needed`), the container clears
        // `pendingRequestId`, which re-runs this effect's cleanup and stops
        // the poller, so this can never poll forever.
        if (hasServerDataLocal) {
          pollFailures += 1;
          if (pollFailures >= POLL_FAILURE_LIMIT) {
            if (!cancelled) {
              setState((prev) => ({
                ...prev,
                error:
                  prev.error ??
                  ({
                    step_id: '',
                    message: 'Lost connection to progress stream.',
                  } as ProgressErrorShape),
              }));
            }
            stopPolling();
            return;
          }
        }
      }
      if (!cancelled && !completedFired) {
        pollTimer = setTimeout(tick, pollIntervalMs);
      }
    };

    pollTimer = setTimeout(tick, pollIntervalMs);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [clientRequestId, surface, baseUrl, fetchImpl, pollIntervalMs]);

  return state;
}

export default useTurnProgress;
